import { type MaybeRefOrGetter, type Ref, computed, toValue } from 'vue';

type RovingItem = { disabled?: boolean };

type OrientationType = 'horizontal' | 'vertical';

type UseRovingTabsOptions<T extends RovingItem> = {
  tablistRef: Readonly<Ref<HTMLElement | null>>;
  items: MaybeRefOrGetter<readonly T[]>;
  selectedIndex: MaybeRefOrGetter<number>;
  onActivate: (item: T, index: number) => void;
  disabled?: MaybeRefOrGetter<boolean>;
  manualActivation?: MaybeRefOrGetter<boolean>;
  orientation?: MaybeRefOrGetter<OrientationType>;
};

function forwardKeyFor(orientation: OrientationType) {
  return orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';
}

function backwardKeyFor(orientation: OrientationType) {
  return orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';
}

function nextPosition(
  key: string,
  current: number,
  count: number,
  forwardKey: string,
) {
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  if (key === forwardKey) return current < 0 ? 0 : (current + 1) % count;
  return current < 0 ? count - 1 : (current - 1 + count) % count;
}

export function useRovingTabs<T extends RovingItem>({
  tablistRef,
  items,
  selectedIndex,
  onActivate,
  disabled,
  manualActivation,
  orientation,
}: UseRovingTabsOptions<T>) {
  const rovingIndex = computed(() => {
    if (toValue(disabled)) return -1;
    const list = toValue(items);
    const selected = toValue(selectedIndex);
    if (list[selected] && !list[selected].disabled) return selected;
    return list.findIndex((item) => !item.disabled);
  });

  function onKeydown(event: KeyboardEvent) {
    const activeOrientation = toValue(orientation) ?? 'horizontal';
    const forwardKey = forwardKeyFor(activeOrientation);
    const navigationKeys = [
      forwardKey,
      backwardKeyFor(activeOrientation),
      'Home',
      'End',
    ];
    if (!navigationKeys.includes(event.key) || toValue(disabled)) return;

    const all = toValue(items).map((item, index) => ({ item, index }));
    if (!all.length) return;
    event.preventDefault();

    const buttons = Array.from(
      tablistRef.value?.querySelectorAll<HTMLElement>('[role="tab"]') ?? [],
    );
    const focusedPosition = all.findIndex(
      ({ index }) => buttons[index] === document.activeElement,
    );
    const activePosition =
      focusedPosition >= 0 ? focusedPosition : rovingIndex.value;
    const next =
      all[nextPosition(event.key, activePosition, all.length, forwardKey)];
    if (!next) return;
    buttons[next.index]?.focus();

    if (toValue(manualActivation) || next.item.disabled) return;
    onActivate(next.item, next.index);
  }

  function tabindexFor(index: number) {
    return index === rovingIndex.value ? 0 : -1;
  }

  return { rovingIndex, onKeydown, tabindexFor };
}
